import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getUserPortfolios } from "../firebase/firestore";
import colors from "../colors";
import "./PortfolioSwitcher.css";

const PortfolioSwitcher = ({ selectedPortfolio, onPortfolioChange }) => {
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      loadPortfolios();
    }
  }, [user]);

  const loadPortfolios = async () => {
    try {
      setLoading(true);
      const result = await getUserPortfolios(user.uid);

      if (result.success) {
        setPortfolios(result.data);
        // If no portfolio is selected, select the first one or combined
        if (!selectedPortfolio && result.data.length > 0) {
          onPortfolioChange("combined");
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePortfolioSelect = (portfolioId) => {
    onPortfolioChange(portfolioId);
  };

  const getPortfolioName = (portfolioId) => {
    if (portfolioId === "combined") return "Combined";
    const portfolio = portfolios.find((p) => p.id === portfolioId);
    return portfolio ? portfolio.name : "Unknown";
  };

  const getPortfolioValue = (portfolioId) => {
    if (portfolioId === "combined") {
      // Calculate combined value from all portfolios
      return portfolios.reduce((total, p) => {
        return total + (p.totalValue || 0) + (p.cashAvailable || 0);
      }, 0);
    }
    const portfolio = portfolios.find((p) => p.id === portfolioId);
    return portfolio
      ? (portfolio.totalValue || 0) + (portfolio.cashAvailable || 0)
      : 0;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="portfolio-buttons">
        <div className="switcher-loading">Loading portfolios...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-buttons">
        <div className="switcher-error">Error loading portfolios: {error}</div>
      </div>
    );
  }

  return (
    <div className="portfolio-buttons">
      {/* Combined Portfolio Button */}
      <button
        className={`portfolio-btn ${
          selectedPortfolio === "combined" ? "active" : ""
        }`}
        onClick={() => handlePortfolioSelect("combined")}
      >
        Combined
      </button>

      {/* Individual Portfolio Buttons */}
      {portfolios.map((portfolio) => (
        <button
          key={portfolio.id}
          className={`portfolio-btn ${
            selectedPortfolio === portfolio.id ? "active" : ""
          }`}
          onClick={() => handlePortfolioSelect(portfolio.id)}
        >
          {portfolio.name}
        </button>
      ))}

      {/* Add New Portfolio Button */}
      <button
        className="portfolio-btn add-portfolio-btn"
        onClick={() => (window.location.href = "/add-investment")}
        title="Create new portfolio"
      >
        + New
      </button>
    </div>
  );
};

export default PortfolioSwitcher;
